import React, { FC, PropsWithChildren, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Stack, Typography, useMediaQuery } from '@mui/material';
import { keyframes } from '@emotion/react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { QRCodeCanvas } from 'qrcode.react';
import { brand, radius, shadow } from './brand';
import { FlowStatus } from '../../../state/slices/playground';

const pulse = keyframes`
    0%, 100% { opacity: .45; }
    50%      { opacity: 1; }
`;

// One-shot on purpose: a repeating glow stops reading as "just happened" and starts
// reading as a warning.
const successGlow = keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(82, 243, 98, .55), 0 2px 8px rgba(3, 19, 61, .06); }
    45%  { box-shadow: 0 0 0 16px rgba(82, 243, 98, 0), 0 12px 34px rgba(82, 243, 98, .30); }
    100% { box-shadow: 0 0 0 0 rgba(82, 243, 98, 0), 0 6px 22px rgba(82, 243, 98, .18); }
`;

const settledGlow = '0 6px 22px rgba(82, 243, 98, .18)';

// Off-screen but readable by assistive technology. Used for the live region, which
// has no visual counterpart because the states it announces are already on screen.
const visuallyHidden = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
} as const;

interface QrPanelProps {
    title: string;
    instruction: string;
    status: FlowStatus;
    requestUri: string | undefined;
    // The QR itself, so the page can centre on the code rather than on the panel
    // around it — the panel is taller than the viewport on most phones.
    qrRef?: RefObject<HTMLDivElement>;
    // Translation key for the failure headline; `error` is the untranslated raw detail.
    errorKey: string | undefined;
    error: string | undefined;
    successMessage: string;
    onStartOver: () => void;
}

export const QrPanel: FC<QrPanelProps & PropsWithChildren> = (
        {title, instruction, status, requestUri, qrRef, errorKey, error, successMessage, onStartOver, children}) => {
    const {t} = useTranslation();
    // A custom scheme like openid4vp:// can only be handed to a wallet app, which in
    // practice means a phone. On a desktop the button silently does nothing, so offer
    // the link to copy instead.
    const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
    const [linkShown, setLinkShown] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) {
            return;
        }
        const timer = window.setTimeout(() => setCopied(false), 2000);
        return () => window.clearTimeout(timer);
    }, [copied]);

    const copyLink = async () => {
        if (!requestUri || !navigator.clipboard) {
            return;
        }
        await navigator.clipboard.writeText(requestUri);
        setCopied(true);
    };

    // One sentence describing where the flow is, announced whenever it changes. Without
    // this a screen reader user gets no signal at all when the wallet responds.
    const announcement = (): string => {
        switch (status) {
            case 'starting':
                return t('qr.starting');
            case 'pending':
                return t('qr.pending');
            case 'success':
                return successMessage;
            case 'fail':
                return t(errorKey ?? 'qr.failed');
            default:
                return '';
        }
    };

    return (
        <Box sx={{
            background: brand.surface,
            border: `1px solid ${status === 'success' ? brand.green : brand.border}`,
            borderRadius: radius.card,
            boxShadow: shadow.card,
            transition: 'border-color .45s ease',
            padding: {xs: '28px 22px', md: '40px'},
            ...(status === 'success' && {
                animation: `${successGlow} 1.5s cubic-bezier(.2,.7,.3,1) both`,
                '@media (prefers-reduced-motion: reduce)': {animation: 'none', boxShadow: settledGlow},
            }),
        }}>
            <Box role="status" aria-live="polite" sx={visuallyHidden}>
                {announcement()}
            </Box>

            <Stack spacing={3} alignItems="center">
                <Typography sx={{color: brand.navy, fontWeight: 700, fontSize: '1.3rem', textAlign: 'center'}}>
                    {title}
                </Typography>

                {status === 'starting' && <CircularProgress sx={{color: brand.blue}} aria-label={t('qr.starting')}/>}

                {status === 'pending' && requestUri && (
                    <>
                        <Typography sx={{color: brand.text, textAlign: 'center', maxWidth: '40ch', lineHeight: 1.6}}>
                            {instruction}
                        </Typography>
                        <Box
                            ref={qrRef}
                            role="img"
                            aria-label={t('qr.qrAlt')}
                            sx={{
                                padding: '18px',
                                background: brand.surface,
                                border: `1px solid ${brand.border}`,
                                borderRadius: radius.card,
                            }}>
                            <QRCodeCanvas
                                value={requestUri}
                                size={240}
                                fgColor={brand.navy}
                                aria-hidden="true"
                                data-testid="qr-code"/>
                        </Box>

                        {isTouchDevice ? (
                            <Button
                                href={requestUri}
                                variant="contained"
                                endIcon={<ArrowForwardIcon sx={{fontSize: 18, color: brand.green}}/>}
                                data-testid="open-in-wallet"
                                sx={{
                                    borderRadius: radius.pill,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    padding: '11px 26px',
                                    boxShadow: 'none',
                                    background: brand.navy,
                                    '&:hover': {background: brand.navy800, boxShadow: 'none'},
                                }}>
                                {t('qr.openInWallet')}
                            </Button>
                        ) : (
                            <Button
                                onClick={copyLink}
                                variant="contained"
                                data-testid="copy-link"
                                sx={{
                                    borderRadius: radius.pill,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    padding: '11px 26px',
                                    boxShadow: 'none',
                                    background: brand.navy,
                                    '&:hover': {background: brand.navy800, boxShadow: 'none'},
                                }}>
                                {copied ? t('qr.copied') : t('qr.copyLink')}
                            </Button>
                        )}

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: brand.blue,
                                animation: `${pulse} 1.6s ease-in-out infinite`,
                                '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
                            }} aria-hidden="true"/>
                            <Typography sx={{color: brand.text, fontSize: '.92rem'}}>
                                {t('qr.pending')}
                            </Typography>
                        </Stack>

                        {/* The raw request URI, for anyone who wants to inspect what the QR
                            actually encodes. Also the only way to reach it on a desktop. */}
                        <Button
                            onClick={() => setLinkShown(shown => !shown)}
                            aria-expanded={linkShown}
                            data-testid="toggle-link"
                            sx={{
                                color: brand.blue,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '.85rem',
                                borderRadius: radius.pill,
                            }}>
                            {linkShown ? t('qr.hideLink') : t('qr.showLink')}
                        </Button>
                        {linkShown && (
                            <Typography
                                component="p"
                                data-testid="request-uri"
                                sx={{
                                    color: brand.text,
                                    fontSize: '.78rem',
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                    maxWidth: '46ch',
                                    textAlign: 'center',
                                    userSelect: 'all',
                                }}>
                                {requestUri}
                            </Typography>
                        )}
                    </>
                )}

                {status === 'success' && (
                    <>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <CheckCircleIcon sx={{color: brand.green, fontSize: 26}} aria-hidden="true"/>
                            <Typography sx={{color: brand.navy, fontWeight: 600}}>{successMessage}</Typography>
                        </Stack>
                        {children}
                    </>
                )}

                {status === 'fail' && (
                    <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                        sx={{
                            width: '100%',
                            background: '#FDF2F2',
                            border: '1px solid #F2C9C9',
                            borderRadius: radius.card,
                            padding: '14px 18px',
                        }}>
                        <ErrorOutlineIcon sx={{color: '#B4342B'}} aria-hidden="true"/>
                        <Stack spacing={0.5}>
                            <Typography sx={{color: '#7A211A'}}>{t(errorKey ?? 'qr.failed')}</Typography>
                            {error && (
                                <Typography
                                    data-testid="error-detail"
                                    sx={{color: '#7A211A', opacity: .75, fontSize: '.82rem', wordBreak: 'break-word'}}>
                                    {error}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                )}

                {status !== 'starting' && (
                    <Button
                        onClick={onStartOver}
                        sx={{
                            color: brand.blue,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: radius.pill,
                        }}>
                        {t('qr.startOver')}
                    </Button>
                )}
            </Stack>
        </Box>
    );
};
